import React, { useState, useCallback, useRef } from 'react';
import { Upload, Copy, Check, AlertCircle, Code, RotateCcw } from 'lucide-react';
import { formatFileSize } from '../config/api';

type OutputTab = 'dataurl' | 'raw' | 'css' | 'html' | 'markdown';

const TABS: { key: OutputTab; label: string }[] = [
  { key: 'dataurl', label: 'Data URL' },
  { key: 'raw', label: 'Raw Base64' },
  { key: 'css', label: 'CSS' },
  { key: 'html', label: 'HTML' },
  { key: 'markdown', label: 'Markdown' },
];

const ImageToBase64: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [base64Data, setBase64Data] = useState('');
  const [dataUrl, setDataUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<OutputTab>('dataurl');
  const [compressEnabled, setCompressEnabled] = useState(false);
  const [quality, setQuality] = useState(85);
  const [compressedDataUrl, setCompressedDataUrl] = useState('');
  const [compressedBase64, setCompressedBase64] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const processImage = useCallback((f: File, compress: boolean, q: number) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setDataUrl(result);
      setPreview(result);
      const commaIndex = result.indexOf(',');
      setBase64Data(commaIndex >= 0 ? result.substring(commaIndex + 1) : result);

      if (compress) {
        // Re-encode as JPEG with quality
        const img = new window.Image();
        img.onload = () => {
          const canvas = canvasRef.current;
          if (!canvas) return;
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          if (!ctx) return;
          ctx.drawImage(img, 0, 0);
          const compressed = canvas.toDataURL('image/jpeg', q / 100);
          setCompressedDataUrl(compressed);
          const ci = compressed.indexOf(',');
          setCompressedBase64(ci >= 0 ? compressed.substring(ci + 1) : compressed);
        };
        img.src = result;
      } else {
        setCompressedDataUrl('');
        setCompressedBase64('');
      }
    };
    reader.readAsDataURL(f);
  }, []);

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const f = selectedFiles[0];
    if (!f.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }
    setFile(f);
    setError(null);
    setCopied(null);
    processImage(f, compressEnabled, quality);
  }, [compressEnabled, quality, processImage]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  // Re-compress when settings change
  const handleCompressToggle = (enabled: boolean) => {
    setCompressEnabled(enabled);
    if (file) processImage(file, enabled, quality);
  };

  const handleQualityChange = (q: number) => {
    setQuality(q);
    if (file && compressEnabled) processImage(file, true, q);
  };

  const getActiveDataUrl = () => compressEnabled && compressedDataUrl ? compressedDataUrl : dataUrl;
  const getActiveBase64 = () => compressEnabled && compressedBase64 ? compressedBase64 : base64Data;

  const getOutputText = (tab: OutputTab): string => {
    const du = getActiveDataUrl();
    const raw = getActiveBase64();
    switch (tab) {
      case 'dataurl': return du;
      case 'raw': return raw;
      case 'css': return `background-image: url(${du});`;
      case 'html': return `<img src="${du}" alt="image" />`;
      case 'markdown': return `![image](${du})`;
    }
  };

  const currentOutput = file ? getOutputText(activeTab) : '';
  const lineCount = currentOutput.split('\n').length;
  const charCount = currentOutput.length;

  const copyToClipboard = async (tab: OutputTab) => {
    const text = getOutputText(tab);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(tab);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setBase64Data('');
    setDataUrl('');
    setCompressedDataUrl('');
    setCompressedBase64('');
    setError(null);
    setCopied(null);
    setCompressEnabled(false);
    setQuality(85);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const activeBase64Len = getActiveBase64().length;
  const activeDataUrlLen = getActiveDataUrl().length;

  return (
    <div className="space-y-6">
      <canvas ref={canvasRef} className="hidden" />

      {/* Header */}
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-violet-100 rounded-full">
            <Code className="w-8 h-8 text-violet-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Image to Base64</h2>
        <p className="text-gray-600">
          Convert images to Base64 with multiple output formats and optional compression.
        </p>
      </div>

      {/* Upload */}
      {!file && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-violet-400 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Drop an image here or click to select
          </h3>
          <p className="text-gray-600">Supports PNG, JPG, WebP, GIF, SVG and more</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {file && (
        <div className="space-y-4">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left panel: Preview + Info */}
            <div className="space-y-4">
              {/* Preview */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Preview</h3>
                <div className="bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center min-h-[150px]">
                  {preview && (
                    <img src={preview} alt="Preview" className="max-w-full max-h-[250px] object-contain" />
                  )}
                </div>
              </div>

              {/* Compression Option */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Compress (JPEG)</label>
                  <button
                    onClick={() => handleCompressToggle(!compressEnabled)}
                    className={`relative w-10 h-5 rounded-full transition-colors ${
                      compressEnabled ? 'bg-violet-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        compressEnabled ? 'translate-x-5' : ''
                      }`}
                    />
                  </button>
                </div>
                {compressEnabled && (
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-gray-500">Quality</span>
                      <span className="text-xs font-bold text-violet-600">{quality}%</span>
                    </div>
                    <input
                      type="range"
                      min="10"
                      max="100"
                      value={quality}
                      onChange={(e) => handleQualityChange(parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-violet-600"
                    />
                    <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                      <span>10%</span>
                      <span>100%</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Size Info Panel */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Size Info</h4>
                <div className="flex justify-between">
                  <span className="text-gray-600">Original file:</span>
                  <span className="font-medium">{formatFileSize(file.size)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Base64 length:</span>
                  <span className="font-medium">{formatFileSize(activeBase64Len)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Data URL length:</span>
                  <span className="font-medium">{formatFileSize(activeDataUrlLen)}</span>
                </div>
                <div className="flex justify-between text-amber-700 font-medium border-t pt-2">
                  <span>Size increase:</span>
                  <span>~{file.size > 0 ? Math.round(((activeBase64Len - file.size) / file.size) * 100) : 0}%</span>
                </div>
                <div className="flex justify-between text-gray-500 text-xs border-t pt-2">
                  <span>Characters:</span>
                  <span>{charCount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-gray-500 text-xs">
                  <span>Lines:</span>
                  <span>{lineCount.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Right panel: Output */}
            <div className="lg:col-span-2">
              {/* Format Tabs */}
              <div className="flex flex-wrap gap-1 mb-2">
                {TABS.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`text-sm font-medium px-3 py-1.5 rounded-lg transition-colors ${
                      activeTab === tab.key
                        ? 'bg-violet-100 text-violet-700'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Output textarea with line numbers feel */}
              <div className="relative">
                <textarea
                  readOnly
                  value={currentOutput}
                  className="w-full h-[400px] p-4 pl-14 bg-gray-50 border border-gray-200 rounded-xl resize-none focus:outline-none text-xs font-mono leading-5"
                />
                {/* Line number column */}
                <div className="absolute left-0 top-0 w-12 h-[400px] bg-gray-100 rounded-l-xl border-r border-gray-200 overflow-hidden pointer-events-none">
                  <div className="p-4 pr-2 text-right text-xs font-mono text-gray-400 leading-5">
                    {Array.from({ length: Math.min(lineCount, 100) }, (_, i) => (
                      <div key={i}>{i + 1}</div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Copy Button */}
              <div className="flex gap-3 mt-3">
                <button
                  onClick={() => copyToClipboard(activeTab)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-violet-600 text-white font-medium rounded-lg hover:bg-violet-700 transition-colors text-sm"
                >
                  {copied === activeTab ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied === activeTab ? 'Copied!' : `Copy ${TABS.find((t) => t.key === activeTab)?.label}`}
                </button>
              </div>

              {/* Quick copy all formats */}
              <div className="mt-3 grid grid-cols-5 gap-1">
                {TABS.filter((t) => t.key !== activeTab).map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => copyToClipboard(tab.key)}
                    className="flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    {copied === tab.key ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Reset */}
          <div className="flex justify-end">
            <button
              onClick={reset}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-300 transition-colors"
            >
              <RotateCcw className="w-5 h-5" />
              Start Over
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageToBase64;
